import React, { ReactElement } from 'react';
import {
  ActivityIndicator, Pressable, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../constants/Colors';
import AlertIcon from './AlertIcon';
import ResolvedIcon from './ResolvedIcon';
import AlertIconMobile from './16pxIcons/AlertIconMobile';
import ResolvedIconMobile from './16pxIcons/ResolvedIconMobile';
import Style from '../constants/Style';
import isMobile from './IsMobile';
import { logEvent } from './LogEvent';
import Button from './Button';

interface AlertWidgetProps {
  title: string,
  message?: string | ReactElement,
  error?: boolean,
  action?: () => void,
  actionTitle?: string,
  cancelTitle?: string,
  closeTitle?: string,
  onModalHide?: () => void,
  loading?: boolean,
  isMessage?: boolean,
  cancelAction?: () => void,
}

export default function AlertWidget({
  title, message, error = true, action, actionTitle, closeTitle,
  onModalHide, loading = false, isMessage = false, cancelTitle, cancelAction,
}: AlertWidgetProps) {
  const renderClose = () => {
    const showActionButton: boolean = !!action && !!actionTitle;

    return <View style={styles.closeButtonContainer}>
      <Button
        affirmative={!(action && actionTitle)}
        style={{
          ...styles.mainButton,
          ...((showActionButton && isMobile) ? { flex: 1 } : {}),
        }}
        onPress={() => {
          logEvent('AlertModal', 'close_alert_modal');
          if (cancelAction) cancelAction(); else if (!actionTitle && action) action();
        }}
        title={showActionButton ? cancelTitle ?? 'Cancel' : closeTitle ?? 'Close'}
        testID='close-pop-up-modal-btn'
      />
      {showActionButton && <Button
        affirmative
        onPress={() => {
          logEvent('AlertModal', 'alert_modal_action');
          action();
        }}
        title={actionTitle}
        testID='alert-modal-confirm-button'
      />}
    </View>;
  };

  return loading ? <ActivityIndicator
    size='large'
    animating={loading}
    style={Style.activityIndicator}
  /> : <View
    {...{ onModalHide }}
    style={{ alignItems: 'center' }}
  >
    <Pressable
      style={{ flex: 1, justifyContent: 'center' }}
      onPress={() => {
        logEvent('AlertModal', 'close_alert_modal');
      }}>
      <View
        style={[
          styles.container, error && styles.containerError, isMessage && styles.containerMessage,
        ]}
        testID='pop-up-modal'
      >
        <View style={[
          styles.iconContainer, error && styles.iconContainerError,
          isMessage && styles.isMessageContainerError,
        ]}>
          {error && (isMobile ? <AlertIconMobile /> : <AlertIcon />)}
          {!error && (isMobile ? <ResolvedIconMobile /> : <ResolvedIcon />)}
        </View>
        <Text style={[
          Style.title, styles.title, error && styles.titleError, isMessage && styles.messageTitle,
        ]}>
          {title}</Text>
        {message && <Text style={styles.description}>{message}</Text>}
        {renderClose()}
      </View>
    </Pressable>
  </View>;
}

const styles = StyleSheet.create({
  container: {
    alignSelf: 'center',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: isMobile ? 16 : 40,
    margin: 10,
    alignItems: 'center',
  },
  containerMessage: {
    borderColor: Colors.light.hereworksBlue500,
  },
  containerError: {
    borderColor: Colors.light.hereworksRed500,
  },
  iconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    borderRadius: 32,
    backgroundColor: Colors.light.hereworksGreen400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerError: {
    backgroundColor: Colors.light.hereworksRed500,
  },
  isMessageContainerError: {
    backgroundColor: Colors.light.hereworksBlue500,
    borderColor: Colors.light.hereworksBlue500,
  },
  title: {
    color: Colors.light.hereworksGreen400,
    marginTop: isMobile ? 24 : 32,
  },
  titleError: {
    color: Colors.light.hereworksRed500,
  },
  messageTitle: {
    color: Colors.light.hereworksBlue500,
  },
  description: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    marginTop: 12,
    textAlign: 'center',
    width: isMobile ? 'auto' : '100%',
  },
  closeButtonContainer: {
    marginTop: isMobile ? 16 : 40,
    flexDirection: 'row',
  },
  mainButton: {
    marginStart: 0,
    flex: undefined,
  },
});
